import { useState } from "react";
import { gw, upgradeWith } from "../gateway.js";
import { readError } from "../errors.js";
import { acceptTerms } from "../api.js";
import Icon from "../ui/Icon.jsx";
import GoogleMark from "../ui/GoogleMark.jsx";

/**
 * 로그인·가입·계정 연결이 **같은 시트**다.
 *
 * 서버 쪽에서 이미 하나다 — `gw.upgrade()` 는 처음 보는 신원이면 계정을 만들고,
 * 이미 있는 신원이면 그쪽으로 로그인한 뒤 익명 기록을 회수한다.
 * 화면에서 「가입」과 「로그인」을 갈라 봤자 사용자만 헷갈린다.
 *
 * mode 는 문구만 바꾼다. 하는 일은 같다.
 *   login — 헤더에서 열었을 때
 *   save  — 결과를 저장할 때 (아카이브 공개 옵트인이 같이 붙는다)
 */
export default function AuthSheet({ mode = "login", onClose, onDone, extra = null, children }) {
  /**
   * ★ 이용약관·개인정보처리방침 동의는 **필수**다.
   * 계정을 만드는 것 자체가 약관에 따른 계약이라, 동의한 적이 없으면 그 약관을
   * 근거로 들 수 없다 — 약관 제6조의 「언제든지 공개를 취소할 수 있습니다」도
   * 그 약관 조항이다. 전에는 링크조차 없었다.
   *
   * 토론이 로그인을 요구하므로 **계정을 만드는 모든 경로가 이 시트를 지난다.**
   * 그래서 여기서 막으면 동의 없는 계정이 생길 수 없다.
   */
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [provider, setProvider] = useState(null); // null | "email"
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const problem = err ? readError(err) : null;

  const copy = mode === "save"
    ? { title: "이 결과, 저장해 둘까요?", lead: "로그인하면 다른 기기에서도 이 결과를 다시 볼 수 있어요. 지금까지 한 토론은 그대로 따라와요." }
    : { title: "로그인", lead: "쓰던 계정이 있으면 그대로 들어와요. 처음이면 이 자리에서 계정이 만들어져요. 지금 브라우저에서 한 토론은 계정으로 따라와요." };

  /**
   * 비밀번호 재설정 메일.
   * 가입돼 있든 아니든 **같은 문구**를 보여준다 — 다르게 보여주면
   * 어떤 이메일이 가입돼 있는지 확인해 주는 꼴이 된다.
   */
  async function sendReset() {
    if (busy || !form.email) return;
    setBusy(true);
    setErr(null);
    try {
      await gw.resetPassword("email", { email: form.email });
      setResetSent(true);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  async function run(id) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await upgradeWith(id, form);
      // 동의 기록은 계정이 생긴 뒤에 남긴다. 버전과 시점은 서버가 붙인다.
      await acceptTerms();
      await extra?.(r);
      onDone?.(r);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10,
        background: "rgba(26,24,32,.55)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <section role="dialog" aria-modal="true" aria-labelledby="auth-title"
        style={{
          width: "100%", maxWidth: 600, boxSizing: "border-box",
          padding: "12px 18px calc(26px + env(safe-area-inset-bottom, 0px))",
          border: "1px solid var(--line)", borderBottom: 0, borderRadius: "32px 32px 0 0",
          background: "var(--paper)",
          display: "flex", flexDirection: "column", gap: 14,
          maxHeight: "92vh", overflowY: "auto",
        }}>
        <span aria-hidden="true" style={{ alignSelf: "center", width: 44, height: 5, borderRadius: 999, background: "var(--ink)" }} />

        {/* 지금 상태를 알려주는 꼬리표지 경고가 아니다. 붉게 두면 바로 아래 에러 카드와
            같은 무게로 읽혀서, 화면에 붉은 것이 세 개가 된다. */}
        {gw.isAnonymous() && (
          <span className="pill quiet" style={{ alignSelf: "flex-start", fontSize: 12 }}>지금은 로그인 없이 하는 중</span>
        )}
        <h2 id="auth-title" className="d" style={{ fontSize: 30, lineHeight: 1.2 }}>{copy.title}</h2>
        <p className="lead">{copy.lead}</p>

        {problem && (
          <div role="alert" className="card alert" style={{ padding: "14px 16px", gap: 4 }}>
            <p style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.5 }}>{problem.title}</p>
            {problem.hint && <p style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.6, color: "var(--body)" }}>{problem.hint}</p>}
          </div>
        )}

        <label className="card" style={{ flexDirection: "row", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderRadius: 4 }}>
          <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)}
            style={{ width: 22, height: 22, flexShrink: 0, marginTop: 1, accentColor: "#15151A" }} />
          <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.6 }}>
            <a href="https://sowhat.run/terms" target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()} style={{ textDecoration: "underline" }}>이용약관</a>
            {"과 "}
            <a href="https://sowhat.run/privacy" target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()} style={{ textDecoration: "underline" }}>개인정보처리방침</a>
            {"에 동의해요 "}
            <span style={{ color: "var(--muted)" }}>(필수)</span>
          </span>
        </label>

        {provider === "email" ? (
          /* 칸 안쪽(라벨↔입력)은 6, 칸과 칸 사이는 16. 전부 10으로 두면 무엇이 한 묶음인지 안 보인다. */
          <form style={{ display: "flex", flexDirection: "column", gap: 16 }}
            onSubmit={(e) => { e.preventDefault(); run("email"); }}>
            <Field id="auth-email" label="이메일" type="email" autoComplete="email"
              value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field id="auth-pw" label="비밀번호" type="password" autoComplete="current-password" minLength={6}
              value={form.password} onChange={(v) => setForm({ ...form, password: v })}
              note="쓰던 이메일이면 그 계정으로 들어가고, 처음이면 새로 만들어져요." />

            <button type="submit" className="btn primary" disabled={busy || !agreedTerms}>
              {busy ? "확인하는 중…" : "이메일로 계속하기"}
            </button>

            {/* 보조 동작 둘을 한 줄에 둔다. 밑줄 친 가운데 정렬 링크를 세로로 쌓으면
                무엇이 주된 길인지 흐려진다. */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 44 }}>
              <button type="button" className="btn plain" style={{ minHeight: 0 }}
                onClick={() => setProvider(null)}>다른 방법으로</button>
              {!resetSent && (form.email ? (
                <button type="button" className="btn plain" style={{ minHeight: 0 }}
                  disabled={busy} onClick={sendReset}>비밀번호를 잊으셨어요?</button>
              ) : (
                <span className="note">재설정은 이메일을 적으면 돼요</span>
              ))}
            </div>

            {resetSent && (
              <p role="status" className="card cream" style={{ padding: "14px 16px", fontSize: 14, fontWeight: 500, lineHeight: 1.7, color: "var(--body)" }}>
                재설정 메일을 보냈어요. 받은 편지함을 확인해 주세요.
                메일이 안 보이면 스팸함도 한 번 보세요.
              </p>
            )}
          </form>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {gw.listProviders().map((p) => (
              <button key={p.id} type="button" className={`btn${p.id === "email" ? " primary" : ""}`}
                disabled={busy || !agreedTerms}
                onClick={() => (p.id === "email" ? setProvider("email") : run(p.id))}>
                {p.id === "google" && <GoogleMark size={20} />}
                {p.id === "email" && <Icon name="mail" size={20} stroke="currentColor" width={2.2} />}
                <span>{p.label}</span>
              </button>
            ))}
          </div>
        )}

        {!agreedTerms && (
          <p className="note" style={{ textAlign: "center" }}>
            동의하셔야 계정을 만들 수 있어요.
          </p>
        )}

        {children}

        {/* 나가는 길은 시트의 다른 내용과 선으로 끊어 둔다 */}
        <button type="button" className="btn plain"
          style={{ borderTop: "1px solid var(--line-soft)", borderRadius: 0, paddingTop: 14, marginTop: 2 }}
          onClick={onClose}>
          {mode === "save" ? "나중에 할게요" : "닫기"}
        </button>
      </section>
    </div>
  );
}

/** 라벨·입력·설명 한 묶음. 묶음 안은 6px 로 붙인다. */
function Field({ id, label, note, value, onChange, ...rest }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* .label 은 11px·faint 라 입력 라벨로 쓰기엔 종이 위 대비가 모자란다 */}
      <label htmlFor={id} style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.02em", color: "var(--muted)" }}>
        {label}
      </label>
      <input id={id} required value={value} onChange={(e) => onChange(e.target.value)}
        style={inputStyle} {...rest} />
      {note && <p className="note" style={{ marginTop: 2 }}>{note}</p>}
    </div>
  );
}

const inputStyle = {
  minHeight: 52,
  width: "100%",
  padding: "0 16px",
  border: "1px solid var(--line)",
  borderRadius: 4,
  background: "var(--white)",
  color: "var(--ink)",
  fontFamily: "inherit",
  fontSize: 16,
  fontWeight: 500,
};
